import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { X, Trophy, Star, Zap, Target } from "lucide-react";

interface CelebrationProps {
  trigger: string;
  message: string;
  points: number;
  achievement?: string;
  onClose: () => void;
  duration?: number;
}

interface CelebrationEvent {
  id: string;
  type: "water_logged" | "goal_reached" | "streak" | "achievement" | "level_up";
  message: string;
  points: number;
  achievement?: string;
  color: string;
  icon: string;
}

export function CelebrationModal({ trigger, message, points, achievement, onClose, duration = 4000 }: CelebrationProps) {
  const [isVisible, setIsVisible] = useState(true);
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    // Start animation
    setTimeout(() => setAnimate(true), 100);
    
    // Auto close after duration
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300);
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const getCelebrationStyle = (trigger: string) => {
    switch (trigger) {
      case "goal_reached":
        return {
          bg: "bg-gradient-to-br from-green-400 to-emerald-600",
          icon: "🎯",
          emoji: "🎉",
          color: "text-white"
        };
      case "achievement":
        return {
          bg: "bg-gradient-to-br from-yellow-400 to-orange-600",
          icon: "🏆",
          emoji: "⭐",
          color: "text-white"
        };
      case "streak":
        return {
          bg: "bg-gradient-to-br from-purple-400 to-pink-600",
          icon: "🔥",
          emoji: "💪",
          color: "text-white"
        };
      case "level_up":
        return {
          bg: "bg-gradient-to-br from-blue-400 to-indigo-600",
          icon: "⚡",
          emoji: "🌟",
          color: "text-white"
        };
      default:
        return {
          bg: "bg-gradient-to-br from-hydro-blue to-blue-600",
          icon: "💧",
          emoji: "👏",
          color: "text-white"
        };
    }
  };

  const style = getCelebrationStyle(trigger);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className={`transform transition-all duration-300 ${animate ? 'scale-100 opacity-100' : 'scale-50 opacity-0'}`}>
        <Card className={`${style.bg} ${style.color} border-0 shadow-2xl max-w-sm mx-4`}>
          <CardContent className="text-center p-6 relative">
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="absolute top-2 right-2 text-white hover:bg-white hover:bg-opacity-20"
            >
              <X className="w-4 h-4" />
            </Button>
            
            <div className="text-6xl mb-3 animate-bounce">
              {style.icon}
            </div>
            
            <h3 className="text-xl font-bold mb-2">
              {achievement || "Great Job!"}
            </h3>
            
            <p className="text-sm opacity-90 mb-4">
              {message}
            </p>
            
            <div className="bg-white bg-opacity-20 rounded-lg p-3 mb-4">
              <div className="text-2xl font-bold">
                +{points} points
              </div>
              <div className="text-xs opacity-80">
                Added to your score
              </div>
            </div>
            
            <div className="text-3xl">
              {style.emoji}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default function RealTimeCelebration() {
  const [celebrations, setCelebrations] = useState<CelebrationEvent[]>([]);
  const [activeCelebration, setActiveCelebration] = useState<CelebrationEvent | null>(null);

  // Demo function to add celebrations (would be triggered by real events)
  const addCelebration = (event: Omit<CelebrationEvent, 'id'>) => {
    const celebration: CelebrationEvent = {
      ...event,
      id: Date.now().toString()
    };
    
    setCelebrations(prev => [celebration, ...prev.slice(0, 4)]); // Keep last 5
    
    // Show celebration modal if none is currently active
    if (!activeCelebration) {
      setActiveCelebration(celebration);
    }
  };

  // Demo celebrations for testing
  const triggerDemo = () => {
    const demos = [
      {
        type: "water_logged" as const,
        message: "Keep up the great hydration!",
        points: 10,
        color: "bg-blue-500",
        icon: "💧"
      },
      {
        type: "goal_reached" as const,
        message: "You've reached your daily target!",
        points: 50,
        achievement: "Daily Goal Reached!",
        color: "bg-green-500",
        icon: "🎯"
      },
      {
        type: "streak" as const,
        message: "5 days in a row - you're on fire!",
        points: 25,
        achievement: "5-Day Streak!",
        color: "bg-purple-500",
        icon: "🔥"
      }
    ];
    
    const randomDemo = demos[Math.floor(Math.random() * demos.length)];
    addCelebration(randomDemo);
  };

  return (
    <div>
      {/* Recent Celebrations Feed */}
      {celebrations.length > 0 && (
        <Card className="mb-4">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-slate-900">🎉 Recent Wins</h4>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={triggerDemo}
                className="text-xs"
                data-testid="trigger-demo-celebration"
              >
                Demo Celebration
              </Button>
            </div>
            <div className="space-y-2">
              {celebrations.slice(0, 3).map((celebration) => (
                <div 
                  key={celebration.id}
                  className="flex items-center space-x-3 p-2 rounded-lg bg-gradient-to-r from-slate-50 to-blue-50 border border-slate-200"
                >
                  <div className="text-lg">{celebration.icon}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {celebration.achievement || "Achievement Unlocked!"}
                    </p>
                    <p className="text-xs text-slate-600 truncate">
                      {celebration.message}
                    </p>
                  </div>
                  <div className="text-sm font-bold text-hydro-blue">
                    +{celebration.points}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Celebration Modal */}
      {activeCelebration && (
        <CelebrationModal
          trigger={activeCelebration.type}
          message={activeCelebration.message}
          points={activeCelebration.points}
          achievement={activeCelebration.achievement}
          onClose={() => setActiveCelebration(null)}
        />
      )}
    </div>
  );
}